// Gantt Toolbar Component - Controls and actions
import React from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, ChevronLeft, ChevronRight, Download,
  Filter, Grid3x3, Layers, Plus, Search, Settings,
  Users, ZoomIn, ZoomOut, Maximize2, Eye, EyeOff,
  FileSpreadsheet, FileText, Image, RefreshCw
} from 'lucide-react';
import { ViewMode, GanttConfig } from '../types/gantt.types';

interface GanttToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  showResourceView: boolean;
  onToggleResourceView: () => void;
  showStats: boolean;
  onToggleStats: () => void;
  onExportPDF: () => void;
  onExportExcel: () => void;
  onCreateTask: () => void;
  config: GanttConfig;
  onConfigChange: (config: Partial<GanttConfig>) => void;
}

export const GanttToolbar: React.FC<GanttToolbarProps> = ({
  viewMode,
  onViewModeChange,
  zoom,
  onZoomChange,
  showFilters,
  onToggleFilters,
  showResourceView,
  onToggleResourceView,
  showStats,
  onToggleStats,
  onExportPDF,
  onExportExcel,
  onCreateTask,
  config,
  onConfigChange
}) => {
  const viewModes: { value: ViewMode; label: string; icon: React.ReactNode }[] = [
    { value: 'day', label: 'Dzień', icon: '1D' },
    { value: 'week', label: 'Tydzień', icon: '1W' },
    { value: 'month', label: 'Miesiąc', icon: '1M' },
    { value: 'quarter', label: 'Kwartał', icon: '3M' },
    { value: 'year', label: 'Rok', icon: '1Y' }
  ];

  return (
    <div className="bg-zinc-900 border-b border-zinc-800 px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* View Mode Selector */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            {viewModes.map((mode) => (
              <button
                key={mode.value}
                onClick={() => onViewModeChange(mode.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === mode.value
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-zinc-700'
                }`}
              >
                {mode.icon}
              </button>
            ))}
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onZoomChange(Math.max(50, zoom - 10))}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              disabled={zoom <= 50}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-400 min-w-[3rem] text-center">
              {zoom}%
            </span>
            <button
              onClick={() => onZoomChange(Math.min(200, zoom + 10))}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              disabled={zoom >= 200}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={() => onZoomChange(100)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors ml-1"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          <div className="w-px h-6 bg-zinc-700" />

          {/* View Options */}
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleFilters}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                showFilters
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filtry</span>
            </button>

            <button
              onClick={onToggleResourceView}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                showResourceView
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <Users className="w-4 h-4" />
              <span className="text-sm">Zasoby</span>
            </button>

            <button
              onClick={onToggleStats}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                showStats
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              {showStats ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="text-sm">Statystyki</span>
            </button>
          </div>

          <div className="w-px h-6 bg-zinc-700" />

          {/* Display Options */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onConfigChange({ showDependencies: !config.showDependencies })}
              className={`p-1.5 rounded-lg transition-all ${
                config.showDependencies
                  ? 'text-purple-400 bg-purple-500/20'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
              title="Pokaż zależności"
            >
              <Grid3x3 className="w-4 h-4" />
            </button>

            <button
              onClick={() => onConfigChange({ showCriticalPath: !config.showCriticalPath })}
              className={`p-1.5 rounded-lg transition-all ${
                config.showCriticalPath
                  ? 'text-red-400 bg-red-500/20'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
              title="Ścieżka krytyczna"
            >
              <Layers className="w-4 h-4" />
            </button>

            <button
              onClick={() => onConfigChange({ showWeekends: !config.showWeekends })}
              className={`p-1.5 rounded-lg transition-all ${
                config.showWeekends
                  ? 'text-yellow-400 bg-yellow-500/20'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
              title="Weekendy"
            >
              <Calendar className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Actions */}
          <button
            onClick={onCreateTask}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:shadow-lg transition-all"
          >
            <Plus className="w-4 h-4" />
            Nowe zadanie
          </button>

          {/* Export Options */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <button
              onClick={onExportPDF}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-zinc-700 rounded-md transition-colors"
              title="Eksportuj do PDF"
            >
              <FileText className="w-4 h-4" />
            </button>
            <button
              onClick={onExportExcel}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-zinc-700 rounded-md transition-colors"
              title="Eksportuj do Excel"
            >
              <FileSpreadsheet className="w-4 h-4" />
            </button>
            <button
              className="p-1.5 text-gray-400 hover:text-white hover:bg-zinc-700 rounded-md transition-colors"
              title="Eksportuj jako obraz"
            >
              <Image className="w-4 h-4" />
            </button>
          </div>

          {/* Settings */}
          <button
            className="p-2 text-gray-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            title="Ustawienia"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};